import React, { useEffect, useMemo, useState } from 'react';
import { Filter, RefreshCw, Download, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

// Segmentler sunucuda hesaplanır (/api/ikas/customers/segments → services/ikasCustomerSegments.js):
// müşteri başına profil (sıklık, ciro, yenileme aralığı, kanal, indirim, marka ve ürün kategorisinden
// evcil hayvan / futbol sinyalleri) + her segment için bir kural. Evcil hayvan grubu yalnız kedi/köpek
// kataloğu olan, futbol grupları krampon/forma satan markalarda (Kixz) gelir.

const API = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';

// Tailwind dinamik sınıfları budadığı için renkler sabit tabloda
const COLORS = {
  amber: { bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  indigo: { bar: 'bg-indigo-500', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  teal: { bar: 'bg-teal-500', chip: 'bg-teal-50 text-teal-700 border-teal-200', dot: 'bg-teal-500' },
  sky: { bar: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
  emerald: { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  violet: { bar: 'bg-violet-500', chip: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  rose: { bar: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  slate: { bar: 'bg-slate-500', chip: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-500' },
  orange: { bar: 'bg-orange-500', chip: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  lime: { bar: 'bg-lime-500', chip: 'bg-lime-50 text-lime-700 border-lime-200', dot: 'bg-lime-500' },
  cyan: { bar: 'bg-cyan-500', chip: 'bg-cyan-50 text-cyan-700 border-cyan-200', dot: 'bg-cyan-500' },
  fuchsia: { bar: 'bg-fuchsia-500', chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200', dot: 'bg-fuchsia-500' },
};

// marka şemasındaki ikas_segment_excluded_customers.reason
const EXCLUDED_LABEL = { b2b: 'B2B', internal: 'internal accounts', bulk: 'bulk buyers' };

const fmtInt = (v) => Number(v || 0).toLocaleString('tr-TR');
const fmtPct = (v) => `${(Number(v || 0) * 100).toLocaleString('tr-TR', { maximumFractionDigits: v < 0.01 ? 2 : 1 })}%`;
const fmtTL = (v) => {
  const n = Number(v || 0);
  if (n >= 1e6) return `₺${(n / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (n >= 1e3) return `₺${(n / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}K`;
  return `₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
};

function headers() {
  const brandId = localStorage.getItem('currentBrandId');
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return { 'X-Brand-ID': brandId, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function IkasCustomSegments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('all');
  const [open, setOpen] = useState({}); // key → { loading, rows, error }

  const load = async (fresh = false) => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/ikas/customers/segments${fresh ? '?fresh=1' : ''}`, { credentials: 'include', headers: headers() });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(js.error || `HTTP ${res.status}`);
      setData(js);
    } catch (e) {
      setError(e.message || 'Could not load segments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.groups || [])
      .filter((g) => group === 'all' || g.id === group)
      .map((g) => ({ ...g, segments: g.segments.filter((s) => !q || s.name.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q)) }))
      .filter((g) => g.segments.length > 0);
  }, [data, group, query]);

  const totalSegments = (data?.groups || []).reduce((a, g) => a + g.segments.length, 0);

  const toggle = async (key) => {
    if (open[key]) { setOpen((o) => { const n = { ...o }; delete n[key]; return n; }); return; }
    setOpen((o) => ({ ...o, [key]: { loading: true, rows: [] } }));
    try {
      const res = await fetch(`${API}/api/ikas/customers/segments/${key}?limit=50`, { credentials: 'include', headers: headers() });
      const js = await res.json();
      if (!res.ok) throw new Error(js.error || `HTTP ${res.status}`);
      setOpen((o) => ({ ...o, [key]: { loading: false, rows: js.rows || [] } }));
    } catch (e) {
      setOpen((o) => ({ ...o, [key]: { loading: false, rows: [], error: e.message } }));
    }
  };

  const [downloading, setDownloading] = useState(null);
  const exportCsv = async (key) => {
    setDownloading(key);
    try {
      const res = await fetch(`${API}/api/ikas/customers/segments/${key}?format=csv`, { credentials: 'include', headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${key}_customers.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/ikaslogoicon.png" alt="Ikas" className="w-10 h-10 object-contain rounded-lg" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Customer Segments</h1>
              <p className="text-sm text-gray-500">
                {data
                  ? <>{totalSegments} segments · {fmtInt(data.totalCustomers)} customers · updated {new Date(data.generatedAt).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}{data.stale ? ' (refreshing…)' : ''}</>
                  : 'Behaviour, lifecycle, replenishment and product-based audiences'}
              </p>
              {data?.channels?.length > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">Sales channels counted: {data.channels.join(', ')} — other channels are left out</p>
              )}
              {data?.excluded?.length > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Excluded from all segments:{' '}
                  {data.excluded.map((x) => `${fmtInt(x.customers)} ${EXCLUDED_LABEL[x.reason] || x.reason} (${fmtTL(x.revenue12m)})`).join(' · ')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search segments…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="text-sm border rounded-md px-3 py-1.5 pl-8 w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <Filter size={14} className="absolute left-2 top-2.5 text-gray-400" />
            </div>
            <button
              className={`p-2 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors ${loading ? 'opacity-50' : ''}`}
              onClick={() => load(true)}
              disabled={loading}
              title="Recalculate"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {data && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => setGroup('all')}
              className={`text-xs px-3 py-1.5 rounded-full border ${group === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              All <span className="opacity-60">{totalSegments}</span>
            </button>
            {data.groups.map((g) => (
              <button
                key={g.id}
                onClick={() => setGroup(g.id)}
                className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${group === g.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${(COLORS[g.color] || COLORS.slate).dot}`} />
                {g.title} <span className="opacity-60">{g.segments.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && !data && (
        <div className="bg-white border rounded-2xl p-10 flex flex-col items-center gap-3 text-sm text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          Building customer profiles… this can take up to 30 seconds the first time.
        </div>
      )}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm">{error}</div>}
      {data && groups.length === 0 && <div className="bg-white border rounded-2xl p-6 text-sm text-gray-500">No segments match your filters.</div>}

      {groups.map((g) => {
        const c = COLORS[g.color] || COLORS.slate;
        const maxShare = Math.max(...g.segments.map((s) => s.share), 0.0001);
        return (
          <div key={g.id} className="bg-white border rounded-2xl p-4 shadow-sm">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{g.title}</h2>
                <p className="text-xs text-gray-500">{g.desc}</p>
              </div>
              <span className="text-xs text-gray-500">{g.segments.length} segments</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.segments.map((s) => {
                const o = open[s.key];
                return (
                  <div key={s.key} className={`border rounded-xl p-3 hover:shadow-sm transition-shadow ${o ? 'md:col-span-2 lg:col-span-3' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900">{s.name}</div>
                        <div className="text-xs text-gray-500">{s.desc}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          className="text-xs px-2 py-0.5 rounded-md border bg-white hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-40"
                          onClick={() => toggle(s.key)}
                          disabled={!s.count}
                        >
                          {o ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Customers
                        </button>
                        <button
                          className="text-xs px-2 py-0.5 rounded-md border bg-white hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-40"
                          onClick={() => exportCsv(s.key)}
                          disabled={!s.count || downloading === s.key}
                          title="Download all customers in this segment"
                        >
                          {downloading === s.key ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} CSV
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex items-end gap-3">
                      <div className="text-2xl font-bold text-gray-800 tabular-nums">{fmtInt(s.count)}</div>
                      <div className="flex-1 pb-1.5">
                        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className={`h-full ${c.bar}`} style={{ width: `${Math.max(s.count ? 2 : 0, (s.share / maxShare) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="mt-1.5 flex justify-between text-xs text-gray-500 tabular-nums">
                      <span>{fmtPct(s.share)} of customers</span>
                      <span>{fmtTL(s.revenue12m)} · {fmtPct(s.revenueShare)} of 12m revenue</span>
                    </div>

                    {o && (
                      <div className="mt-3 border-t pt-2">
                        {o.loading ? (
                          <div className="flex items-center gap-2 text-xs text-gray-500 py-3"><Loader2 size={14} className="animate-spin" /> Loading customers…</div>
                        ) : o.error ? (
                          <div className="text-xs text-red-600 py-2">{o.error}</div>
                        ) : (
                          <>
                            <div className="text-xs text-gray-500 mb-1">Top {o.rows.length} by 12-month revenue — download CSV for all {fmtInt(s.count)}</div>
                            <div className="overflow-x-auto max-h-72 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-white">
                                  <tr className="text-left text-gray-500 border-b">
                                    <th className="py-1.5 pr-3 font-medium">Customer ID</th>
                                    <th className="py-1.5 pr-3 font-medium text-right">Orders</th>
                                    <th className="py-1.5 pr-3 font-medium text-right">Orders 12m</th>
                                    <th className="py-1.5 pr-3 font-medium text-right">Revenue 12m</th>
                                    <th className="py-1.5 pr-3 font-medium">First order</th>
                                    <th className="py-1.5 pr-3 font-medium">Last order</th>
                                    <th className="py-1.5 pr-3 font-medium text-right">Days since</th>
                                    <th className="py-1.5 font-medium text-right">Avg gap (days)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {o.rows.map((r) => (
                                    <tr key={r.customer_id} className="border-b border-gray-50 tabular-nums">
                                      <td className="py-1 pr-3 font-mono text-gray-600">{r.customer_id}</td>
                                      <td className="py-1 pr-3 text-right">{fmtInt(r.orders)}</td>
                                      <td className="py-1 pr-3 text-right">{fmtInt(r.orders_12m)}</td>
                                      <td className="py-1 pr-3 text-right">{fmtTL(r.revenue_12m)}</td>
                                      <td className="py-1 pr-3">{r.first_order}</td>
                                      <td className="py-1 pr-3">{r.last_order}</td>
                                      <td className="py-1 pr-3 text-right">{fmtInt(r.days_since)}</td>
                                      <td className="py-1 text-right">{r.avg_gap_days ?? '–'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
